import React, { useEffect, useState } from 'react'
import { Modal, Row, Col } from 'react-bootstrap'
import Button from '@components/common/Button/Button.component'
import InputField from '@components/common/InputField/InputField.component'
import { IDentalChart, IPerioChart } from '@dataTypes/chartingFeature.types'
import { INote } from '@dataTypes/note.type'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { formatProviderDisplayName } from '@utils/provider-display.utils'
import { DataGridComponent } from '@components/common'
import { EmptyState } from '@components/ui'
import {
  RecordClickEventArgs,
  RowSelectEventArgs,
  ColumnModel,
} from '@syncfusion/ej2-react-grids'
import {
  setCreateDentalChart,
  setCreatePerioChart,
  setPreferCreateDentalChart,
  setPreferCreatePerioChart,
  setSelectedDentalChart,
  setSelectedPerioChart,
} from '@store/reducers/charting.reducer'
import { EmptyDentalChart, EmptyPerioChart } from '@constants/Charting'

interface CreateExistingChartModalProps {
  show: boolean
  onHide: () => void
  chartType: 'dental' | 'perio'
  searchTerm: string
  onSearchChange: (term: string) => void
}

const CreateExistingChartModal: React.FC<CreateExistingChartModalProps> = ({
  show,
  onHide,
  chartType,
  searchTerm,
  onSearchChange,
}) => {
  const titleMap = {
    dental: 'Dental',
    perio: 'Perio',
  }

  const columns: ColumnModel[] = [
    {
      field: 'date',
      headerText: 'Date',
      width: '100',
      template: (row: IDentalChart | IPerioChart): JSX.Element => (
        <span>
          {new Date(row.date_time_created_at ?? '').toLocaleDateString()}
        </span>
      ),
    },
    {
      field: 'title',
      headerText: 'Chart name',
      width: '200',
    },
    {
      field: 'provider_name',
      headerText: 'Provider',
      width: '200',
    },
    {
      field: 'notes',
      headerText: 'Notes',
      width: '250',
      template: (row: IDentalChart | IPerioChart): JSX.Element => {
        const notes = row.notes || []
        if (notes.length === 0) {
          return <span>-</span>
        }
        return (
          <div
            className="text-wrap text-break"
            style={{ maxWidth: '220px', lineHeight: '1.4' }}
          >
            {notes.map((note: INote) => note.body).join(', ')}
          </div>
        )
      },
    },
  ]
  const dispatch = useAppDispatch()
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const chartingLoading = useAppSelector((state) => state.charting.loading)
  const allChartHistory = useAppSelector((state) =>
    chartType === 'dental'
      ? state.charting.dentalCharts
      : state.charting.perioCharts,
  )

  // Filter charts to only show those belonging to the current patient
  const chartHistory =
    selectedPatient && !chartingLoading
      ? (allChartHistory as (IDentalChart | IPerioChart)[]).filter(
          (chart) => chart.patient_id === selectedPatient.patient_id,
        )
      : []

  // Filter chart history based on search term
  const filteredChartHistory = searchTerm.trim()
    ? chartType === 'dental'
      ? (chartHistory as IDentalChart[]).filter((chart: IDentalChart) => {
          const searchLower = searchTerm.toLowerCase()
          const notesText =
            chart.notes
              ?.map((note: INote) => `${note.title} ${note.body}`)
              .join(' ') || ''
          return (
            chart.title?.toLowerCase().includes(searchLower) ||
            notesText.toLowerCase().includes(searchLower) ||
            chart.date_time_created_at?.toLowerCase().includes(searchLower) ||
            chart.provider?.first_name?.toLowerCase().includes(searchLower) ||
            chart.provider?.last_name?.toLowerCase().includes(searchLower)
          )
        })
      : (chartHistory as IPerioChart[]).filter((chart: IPerioChart) => {
          const searchLower = searchTerm.toLowerCase()
          const notesText =
            chart.notes
              ?.map((note: INote) => `${note.title} ${note.body}`)
              .join(' ') || ''
          return (
            chart.title?.toLowerCase().includes(searchLower) ||
            notesText.toLowerCase().includes(searchLower) ||
            chart.date_time_created_at?.toLowerCase().includes(searchLower) ||
            chart.provider?.first_name?.toLowerCase().includes(searchLower) ||
            chart.provider?.last_name?.toLowerCase().includes(searchLower)
          )
        })
    : chartHistory

  const [selectedChart, setSelectedChart] = useState<
    IPerioChart | IDentalChart | null
  >(null)

  const chartNoun = chartType === 'dental' ? 'dental charts' : 'perio charts'
  const hasNoRecords = !chartingLoading && chartHistory.length === 0
  const showEmptyState = !chartingLoading && filteredChartHistory.length === 0
  const emptyDescription = !selectedPatient
    ? 'Select a patient to view their charts.'
    : hasNoRecords
      ? `You don't have any ${chartNoun} on record yet.`
      : `No ${chartNoun} match your search.`

  useEffect(() => {
    if (showEmptyState) {
      setSelectedChart(null)
    }
  }, [showEmptyState])

  const onSelectHistory = (row: RowSelectEventArgs): void => {
    const args = row as RecordClickEventArgs
    const chart = row.data as IPerioChart | IDentalChart

    if (args.name === 'rowDeselected') {
      setSelectedChart(null)
    } else {
      setSelectedChart(chart)
    }
  }
  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Create from existing {titleMap[chartType]} Chart
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="overflow-auto" style={{ maxHeight: '75vh' }}>
        <Row className="mb-1">
          <Col className="d-flex justify-content-end col-existing-chart-search">
            <InputField
              className="search-align-right"
              type="text"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={`Search ${titleMap[chartType]} Chart`}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            {!showEmptyState && (
              <label className="mb-1">
                {titleMap[chartType]} Charting history
              </label>
            )}
            <div className="charting-history">
              {showEmptyState ? (
                <EmptyState
                  className="charting-history-empty"
                  iconClass="mgc mgc_document_fill"
                  title={`No ${chartNoun} found`}
                  description={emptyDescription}
                />
              ) : (
                <DataGridComponent
                  columns={columns}
                  data={filteredChartHistory.map(
                    (chart: IPerioChart | IDentalChart) => ({
                      ...chart,
                      provider_name: formatProviderDisplayName(chart.provider),
                      date: chart.date_time_created_at,
                    }),
                  )}
                  pageSize={10}
                  selectionSettings={{ type: 'Single' }}
                  rowSelected={onSelectHistory}
                  rowDeselected={onSelectHistory}
                  allowPaging={true}
                  customCSS="datatable-table charting-history-table"
                  sortSettings={{
                    columns: [{ field: 'date', direction: 'Descending' }],
                  }}
                />
              )}
            </div>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onHide} className="button-secondary me-1">
          Cancel
        </Button>
        <Button
          onClick={() => {
            if (selectedChart) {
              if (chartType === 'dental') {
                dispatch(setSelectedDentalChart(null))
                dispatch(setPreferCreateDentalChart(true))
                dispatch(
                  setCreateDentalChart({
                    ...EmptyDentalChart,
                    patient_id: selectedChart.patient_id,
                    dental_data: (selectedChart as IDentalChart).dental_data,
                  }),
                )
              } else {
                dispatch(setSelectedPerioChart(null))
                dispatch(setPreferCreatePerioChart(true))
                dispatch(
                  setCreatePerioChart({
                    ...EmptyPerioChart,
                    patient_id: selectedChart.patient_id,
                    perio_data: (selectedChart as IPerioChart).perio_data,
                  }),
                )
              }
              onHide()
            }
          }}
          className="button-primary"
          disabled={!selectedChart}
        >
          Create chart
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default CreateExistingChartModal
